/**
 * Validates a user_type_points parser expression according to the exact scheme parser logic.
 *
 * Supported forms:
 * - Percentage: "50%", "(100%)"
 * - Multiplier: "key * 0.2", "(key * 0.5)"
 * - Passthrough: "key"
 * - Numeric static value: "20", "(20)"
 * - If-Else conditional: "if(key > 1000) ? 20 : 10" or recursive if-else chains.
 *
 * @param {string} parser Expression string to validate
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validateSchemeParserExpression(parser) {
  if (typeof parser !== "string" || !parser.trim()) {
    return { valid: false, error: "Parser expression cannot be empty." };
  }

  try {
    evaluateSchemeParserExpression(parser.trim(), 1000);
    return { valid: true, error: null };
  } catch (err) {
    return { valid: false, error: err.message };
  }
}

function evaluateSchemeParserExpression(parser, keyValue) {
  const trimmed = parser.trim();

  if (trimmed.startsWith("if(")) {
    const condMatch = trimmed.match(
      /^if\(key\s*(>=|<=|!=|==|>|<)\s*([\d.]+)\)\s*\?\s*/
    );
    if (!condMatch) {
      throw new Error(`Invalid if-expression syntax: "${trimmed}"`);
    }

    const operator = condMatch[1];
    const threshold = parseFloat(condMatch[2]);
    const rest = trimmed.slice(condMatch[0].length); // "thenExpr : elseExpr"

    const colonIdx = rest.indexOf(" : ");
    if (colonIdx === -1) {
      throw new Error(`Missing else branch " : " in: "${trimmed}"`);
    }

    const thenExpr = rest.slice(0, colonIdx).trim();
    const elseExpr = rest.slice(colonIdx + 3).trim();

    const condResult = evalCondition(keyValue, operator, threshold);

    if (condResult) {
      parseValueExpr(thenExpr, keyValue);
    }
    // Always validate elseExpr to ensure nested expressions in else branch are syntax-correct
    evaluateSchemeParserExpression(elseExpr, keyValue);

    return 0;
  }

  return parseValueExpr(trimmed, keyValue);
}

function evalCondition(keyValue, operator, threshold) {
  switch (operator) {
    case ">":
      return keyValue > threshold;
    case "<":
      return keyValue < threshold;
    case ">=":
      return keyValue >= threshold;
    case "<=":
      return keyValue <= threshold;
    case "==":
      return keyValue === threshold;
    case "!=":
      return keyValue !== threshold;
    default:
      return false;
  }
}

function parseValueExpr(expr, keyValue) {
  const t = expr.trim();

  const pct = t.match(/^\(?([\d.]+)%\)?$/);
  if (pct) return (parseFloat(pct[1]) * keyValue) / 100;

  const mul = t.match(/^\(?key\s*\*\s*([\d.]+)\)?$/);
  if (mul) return keyValue * parseFloat(mul[1]);

  if (t === "key") return keyValue;

  const num = t.match(/^\(?([\d.]+)\)?$/);
  if (num) return parseFloat(num[1]);

  throw new Error(`Unrecognized value expression: "${expr}"`);
}
